import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { FolderKanban, Flag, Bug, CheckCircle2, Clock3, ChevronRight, Check } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { useAuth } from "../../context/AuthContext";

const STATUS_TINT = {
  "Not Started": "bg-neutral-100 text-neutral-700 border-neutral-300",
  "In Progress": "bg-blue-50 text-blue-700 border-blue-300",
  "On Hold": "bg-amber-50 text-amber-700 border-amber-300",
  "Completed": "bg-emerald-50 text-emerald-700 border-emerald-300",
  "Cancelled": "bg-rose-50 text-rose-700 border-rose-300",
};
const MS_TINT = {
  "Planned": "bg-neutral-100 text-neutral-700 border-neutral-300",
  "In Progress": "bg-blue-50 text-blue-700 border-blue-300",
  "Completed": "bg-emerald-50 text-emerald-700 border-emerald-300",
  "Delayed": "bg-rose-50 text-rose-700 border-rose-300",
};
const BUG_SEV = {
  "Low": "bg-neutral-100 text-neutral-700 border-neutral-300",
  "Medium": "bg-blue-50 text-blue-700 border-blue-300",
  "High": "bg-amber-50 text-amber-700 border-amber-300",
  "Critical": "bg-rose-50 text-rose-700 border-rose-300",
};

export default function PortalProjects() {
  const { brandColor } = useOutletContext();
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [approveTarget, setApproveTarget] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/portal/projects");
      setProjects(data);
      if (data.length && !activeId) setActiveId(data[0].id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading) return <div className="p-8 text-neutral-500">Loading…</div>;

  const accent = brandColor || "#F97316";
  const active = projects.find(p => p.id === activeId);

  return (
    <div className="p-6 md:p-8 space-y-6 max-w-[1400px]" data-testid="portal-projects">
      <div>
        <div className="text-[11px] font-mono uppercase tracking-[0.2em]" style={{ color: accent }}>Delivery</div>
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight mt-1">Your Projects</h1>
        <p className="text-neutral-600 text-sm mt-1">{projects.length} {projects.length === 1 ? "project" : "projects"} in flight</p>
      </div>

      {projects.length === 0 && (
        <div className="panel p-10 text-center text-neutral-500">
          <FolderKanban className="w-8 h-8 mx-auto mb-2 opacity-40" />
          <div className="text-lg font-bold text-neutral-900">No projects yet</div>
          <div className="text-sm mt-1">Once your team kicks off a dev project for you, it'll show up here.</div>
        </div>
      )}

      {projects.length > 0 && (
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left: project cards */}
          <div className="space-y-3 lg:col-span-1">
            {projects.map(p => {
              const isActive = p.id === activeId;
              const pctTasks = p.counts.tasks_total ? Math.round(p.counts.tasks_done / p.counts.tasks_total * 100) : 0;
              return (
                <button
                  key={p.id}
                  onClick={() => setActiveId(p.id)}
                  data-testid={`portal-project-${p.id}`}
                  className={`w-full text-left panel p-4 transition-all ${isActive ? "border-2" : "panel-hover"}`}
                  style={isActive ? { borderColor: accent } : {}}
                >
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="text-[10px] font-mono text-neutral-500">{p.project_id}</span>
                    <span className={`text-[10px] font-mono px-2 py-0.5 rounded border ${STATUS_TINT[p.status] || ""}`}>{p.status}</span>
                  </div>
                  <div className="font-bold text-sm">{p.project_name}</div>
                  <div className="text-[11px] font-mono text-neutral-500 mt-0.5">{p.project_type} · {p.current_stage}</div>
                  <div className="mt-3 h-1.5 rounded-full bg-neutral-100 overflow-hidden">
                    <div className="h-full transition-all" style={{ width: `${pctTasks}%`, background: `linear-gradient(90deg, ${accent}, ${accent}CC)` }} />
                  </div>
                  <div className="flex items-center gap-3 text-[10px] font-mono text-neutral-500 mt-2">
                    <span><Flag className="w-3 h-3 inline mr-0.5" /> {p.counts.milestones_done}/{p.counts.milestones_total}</span>
                    <span><Bug className="w-3 h-3 inline mr-0.5" /> {p.counts.bugs_open} open</span>
                    <span>{pctTasks}% tasks</span>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Right: active project detail */}
          <div className="lg:col-span-2 space-y-6">
            {active && <ProjectDetailBlock p={active} accent={accent} onApprove={setApproveTarget} />}
          </div>
        </div>
      )}
      <ApproveMilestoneDialog target={approveTarget} accent={accent} defaultName={user?.name} onClose={()=>setApproveTarget(null)} onApproved={load} />
    </div>
  );
}

function ApproveMilestoneDialog({ target, accent, defaultName, onClose, onApproved }) {
  const [signature, setSignature] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (target) { setSignature(defaultName || ""); setNotes(""); } }, [target, defaultName]);
  if (!target) return null;
  const submit = async () => {
    if (!signature.trim()) { toast.error("Type your name to sign off"); return; }
    setBusy(true);
    try {
      await api.post(`/portal/milestones/${target.id}/approve`, { signature: signature.trim(), notes: notes.trim() });
      toast.success("Milestone approved");
      onClose(); onApproved?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Approval failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!target} onOpenChange={(v)=>!v && onClose()}>
      <DialogContent className="bg-white border-neutral-200 max-w-md" data-testid="milestone-approve-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">Sign off on this milestone</DialogTitle>
          <DialogDescription className="text-neutral-600 text-xs">
            You're approving <strong className="text-neutral-800">{target.name}</strong> ({target.milestone_id}). This tells your team it's delivered and clears them to invoice.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-[10px] uppercase font-mono tracking-wider text-neutral-500">Type your name as signature</Label>
            <Input value={signature} onChange={e=>setSignature(e.target.value)} placeholder="Your full name" data-testid="ms-signature-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[10px] uppercase font-mono tracking-wider text-neutral-500">Notes (optional)</Label>
            <Textarea rows={3} value={notes} onChange={e=>setNotes(e.target.value)} placeholder="Anything you want the team to know…" data-testid="ms-signature-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="ms-approve-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy || !signature.trim()} className="text-white hover:opacity-90" style={{ background: accent }} data-testid="ms-approve-confirm">
            {busy ? "Signing…" : "Approve Milestone"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ProjectDetailBlock({ p, accent, onApprove }) {
  const stages = p.workflow_stages || [];
  const currentIdx = stages.indexOf(p.current_stage);

  return (
    <>
      {/* Header banner */}
      <div className="panel p-6 relative overflow-hidden">
        <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full blur-3xl opacity-30" style={{ background: accent }} />
        <div className="relative">
          <div className="text-[10px] font-mono uppercase text-neutral-500">{p.project_id} · {p.project_type}</div>
          <h2 className="text-2xl font-extrabold tracking-tight mt-1">{p.project_name}</h2>
          {p.description && <p className="text-sm text-neutral-600 mt-1">{p.description}</p>}
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <span className={`text-[10px] font-mono px-2 py-0.5 rounded border ${STATUS_TINT[p.status] || ""}`}>{p.status}</span>
            <span className="text-[10px] font-mono text-neutral-500">Due {p.expected_completion_date || "—"}</span>
          </div>
        </div>
      </div>

      {/* Pipeline */}
      <div className="panel p-5">
        <div className="text-[10px] font-mono uppercase tracking-[0.2em] mb-2" style={{ color: accent }}>Pipeline</div>
        <div className="text-sm text-neutral-600 mb-3">Stage {currentIdx + 1} of {stages.length}</div>
        <div className="flex gap-2 flex-wrap">
          {stages.map((s, i) => {
            const done = i < currentIdx, current = i === currentIdx;
            return (
              <div
                key={s}
                className="text-[10px] font-mono px-2.5 py-1.5 rounded-lg border flex items-center gap-1.5"
                style={current ? { background: `${accent}1A`, borderColor: `${accent}66`, color: accent }
                    : done ? { background: "#ECFDF5", borderColor: "#A7F3D0", color: "#047857" }
                    : { background: "#FFFFFF", borderColor: "#E4E7EC", color: "#525252" }}
                data-testid={`portal-stage-${p.id}-${i}`}
              >
                {done && <Check className="w-3 h-3" />}
                {s}
                {i < stages.length - 1 && <ChevronRight className="w-3 h-3 opacity-40 ml-1" />}
              </div>
            );
          })}
        </div>
      </div>

      {/* Milestones */}
      <div className="panel p-5">
        <div className="flex items-center gap-2 mb-3">
          <Flag className="w-4 h-4" style={{ color: accent }} />
          <h3 className="font-bold">Milestones ({p.milestones.length})</h3>
        </div>
        {p.milestones.length === 0 ? (
          <div className="text-sm text-neutral-500">No milestones yet.</div>
        ) : (
          <div className="space-y-2">
            {p.milestones.map(m => (
              <div key={m.id} className="flex items-start gap-3 p-3 rounded-lg border border-neutral-200 bg-white" data-testid={`portal-ms-${m.id}`}>
                <div className="w-8 h-8 rounded-lg grid place-items-center" style={{ background: `${accent}1A`, border: `1px solid ${accent}40` }}>
                  {m.client_approved ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : m.status === "Completed" ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <Flag className="w-4 h-4" style={{ color: accent }} />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] font-mono text-neutral-500">{m.milestone_id}</span>
                    <span className={`text-[10px] font-mono px-2 py-0.5 rounded border ${MS_TINT[m.status] || ""}`}>{m.status}</span>
                    {m.due_date && <span className="text-[10px] font-mono text-neutral-500 flex items-center gap-1"><Clock3 className="w-3 h-3" /> {m.due_date}</span>}
                    {m.client_approved && (
                      <span className="text-[10px] font-mono px-2 py-0.5 rounded border bg-emerald-50 text-emerald-700 border-emerald-300">✓ You signed off · {m.client_approved_at?.slice(0,10)}</span>
                    )}
                    {m.client_approval_required && !m.client_approved && m.status === "Completed" && (
                      <span className="text-[10px] font-mono px-2 py-0.5 rounded border bg-amber-50 text-amber-700 border-amber-300">Awaiting your sign-off</span>
                    )}
                  </div>
                  <div className="text-sm font-semibold mt-1">{m.name}</div>
                  {m.description && <div className="text-xs text-neutral-600 mt-1 whitespace-pre-wrap">{m.description}</div>}
                  {m.client_approval_required && !m.client_approved && m.status === "Completed" && (
                    <button
                      onClick={()=>onApprove?.(m)}
                      data-testid={`approve-milestone-${m.id}`}
                      className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white hover:opacity-90 transition-opacity"
                      style={{ background: accent }}
                    >
                      <Check className="w-3.5 h-3.5" /> Approve Milestone
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Bugs */}
      <div className="panel p-5">
        <div className="flex items-center gap-2 mb-3">
          <Bug className="w-4 h-4 text-rose-600" />
          <h3 className="font-bold">Issues ({p.bugs.length})</h3>
          <span className="text-[10px] font-mono px-2 py-0.5 rounded border bg-rose-50 text-rose-700 border-rose-300 ml-auto">
            {p.counts.bugs_open} open
          </span>
        </div>
        {p.bugs.length === 0 ? (
          <div className="text-sm text-neutral-500">No issues reported yet.</div>
        ) : (
          <div className="space-y-2">
            {p.bugs.slice(0, 20).map(b => (
              <div key={b.id} className="p-3 rounded-lg border border-neutral-200 bg-white" data-testid={`portal-bug-${b.id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] font-mono text-neutral-500">{b.bug_id}</span>
                  <span className={`text-[10px] font-mono px-2 py-0.5 rounded border ${BUG_SEV[b.severity] || ""}`}>{b.severity}</span>
                  <span className="text-[10px] font-mono text-neutral-600">{b.status}</span>
                </div>
                <div className="text-sm font-semibold mt-1">{b.title}</div>
                {b.description && <div className="text-xs text-neutral-600 mt-1 whitespace-pre-wrap">{b.description}</div>}
                <div className="text-[10px] font-mono text-neutral-500 mt-1">Raised by {b.reporter_name || "—"}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
